<template>
  <div class="section-card">
    <div class="section-header">
      <div class="section-title">
        <div class="icon-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
          </svg>
        </div>
        Sales Volume
      </div>
      <div class="section-actions">
        <div class="time-filter">
          <button class="time-filter-btn" :class="{ active: selectedPeriod === '7d' }" @click="setPeriod('7d')">7d</button>
          <button class="time-filter-btn" :class="{ active: selectedPeriod === '30d' }" @click="setPeriod('30d')">30d</button>
        </div>
      </div>
    </div>

    <div class="chart-container">
      <div class="chart-wrapper" ref="chartWrapper" @mousemove="onMouseMove" @mouseleave="onMouseLeave">
        <canvas ref="salesCanvas"></canvas>
        <div class="chart-tooltip" ref="chartTooltip" :class="{ visible: tooltipVisible }">
          <div class="chart-tooltip-date">{{ tooltipDate }}</div>
          <div class="chart-tooltip-value">{{ tooltipValue }}</div>
          <div class="chart-tooltip-sub">{{ tooltipSub }}</div>
        </div>
      </div>

      <div class="sales-stats">
        <div class="sales-stat">
          <div class="sales-stat-label">Daily Average</div>
          <div class="sales-stat-value">{{ dailyAverageValue }} units</div>
        </div>
        <div class="sales-stat">
          <div class="sales-stat-label">Last 7 Days</div>
          <div class="sales-stat-value">{{ last7DaysValue }} units <span class="trend" :class="last7DaysChangeValue >= 0 ? 'up' : 'down'">{{ last7DaysChangeValue >= 0 ? '↑' : '↓' }} {{ Math.abs(last7DaysChangeValue) }}%</span></div>
        </div>
        <div class="sales-stat">
          <div class="sales-stat-label">Est. Monthly Revenue</div>
          <div class="sales-stat-value">{{ currencySymbol }}{{ monthlyRevenueValue }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrencyForMarketplace } from '../../utils/currency'

var CHART_PAD = { top: 12, right: 16, bottom: 28, left: 40 }
var CHART_NAVY = '#1B4B82'

export default {
  name: 'SalesVolumeChart',
  props: {
    salesData: {
      type: Object,
      required: true
    },
    marketplace: {
      type: String,
      default: ''
    }
  },
  data: function() {
    return {
      selectedPeriod: '30d',
      hoveredIdx: null,
      tooltipVisible: false,
      tooltipDate: '',
      tooltipValue: '',
      tooltipSub: ''
    }
  },
  computed: {
    currencySymbol: function () {
      return getCurrencyForMarketplace(this.marketplace)
    },
    daysToShow: function() {
      var map = { '7d': 7, '30d': 30, '90d': 90 }
      return map[this.selectedPeriod] || 30
    },
    chartData: function() {
      var allData = this.salesData.chartData || this.salesData.chart_data || []
      if (allData.length === 0) return []
      return allData.slice(-this.daysToShow)
    },
    estimatedUnitPrice: function() {
      var avg = this.dailyAverageValue
      var revenue = this.salesData.estMonthlyRevenue ||
                    (this.salesData.estimated_monthly_revenue && this.salesData.estimated_monthly_revenue.dollars) || 0
      if (avg > 0 && revenue > 0) return revenue / (avg * 30)
      return 17
    },
    dailyAverageValue: function() {
      return this.salesData.dailyAverage || this.salesData.daily_average || 0
    },
    last7DaysValue: function() {
      if (this.salesData.last7Days !== undefined) return this.salesData.last7Days
      if (this.salesData.last_7_days && this.salesData.last_7_days.units !== undefined) return this.salesData.last_7_days.units
      return 0
    },
    last7DaysChangeValue: function() {
      if (this.salesData.last7DaysChange !== undefined) return this.salesData.last7DaysChange
      if (this.salesData.last_7_days && this.salesData.last_7_days.percentage_change !== undefined) return this.salesData.last_7_days.percentage_change
      return 0
    },
    monthlyRevenueValue: function() {
      if (this.salesData.estMonthlyRevenue !== undefined) return this.salesData.estMonthlyRevenue.toLocaleString()
      if (this.salesData.estimated_monthly_revenue && this.salesData.estimated_monthly_revenue.dollars !== undefined) return this.salesData.estimated_monthly_revenue.dollars.toLocaleString()
      return '0'
    }
  },
  methods: {
    setPeriod: function(period) {
      this.selectedPeriod = period
      this.hoveredIdx = null
      this.tooltipVisible = false
      var self = this
      this.$nextTick(function() { self.drawChart(null) })
    },
    parseDate: function(dateStr) {
      if (!dateStr) return new Date()
      var s = String(dateStr)
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        return new Date(s + 'T12:00:00')
      }
      var months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
      var parts = s.split(' ')
      if (parts.length === 2 && months[parts[0]] !== undefined) {
        return new Date(2026, months[parts[0]], parseInt(parts[1]))
      }
      return new Date(s)
    },
    formatDate: function(dateStr) {
      var d = this.parseDate(dateStr)
      if (isNaN(d.getTime())) return String(dateStr)
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    drawChart: function(hIdx) {
      var wrapper = this.$refs.chartWrapper
      var canvas = this.$refs.salesCanvas
      if (!wrapper || !canvas) return

      var data = this.chartData
      if (!data || data.length === 0) return

      var ctx = canvas.getContext('2d')
      var dpr = window.devicePixelRatio || 1
      var W = wrapper.clientWidth
      var H = wrapper.clientHeight
      canvas.width = W * dpr
      canvas.height = H * dpr
      canvas.style.width = W + 'px'
      canvas.style.height = H + 'px'
      ctx.scale(dpr, dpr)
      ctx.clearRect(0, 0, W, H)

      var PAD = CHART_PAD
      var cw = W - PAD.left - PAD.right
      var ch = H - PAD.top - PAD.bottom
      var values = data.map(function(d) { return d.units || 0 })
      var maxV = Math.max.apply(null, values)
      var minV = Math.max(0, Math.min.apply(null, values) - 4)
      var range = maxV - minV + 1

      function sy(v) { return PAD.top + ch - ((v - minV) / range) * ch }
      function sx(i) { return PAD.left + (data.length > 1 ? (i / (data.length - 1)) * cw : cw / 2) }

      ctx.font = '11px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      var gridFracs = [0, 0.33, 0.66, 1]
      for (var g = 0; g < gridFracs.length; g++) {
        var v = Math.round(minV + gridFracs[g] * (maxV - minV))
        var y = sy(v)
        ctx.strokeStyle = '#E2E8F0'
        ctx.lineWidth = 1
        ctx.setLineDash([4, 4])
        ctx.beginPath()
        ctx.moveTo(PAD.left, y)
        ctx.lineTo(W - PAD.right, y)
        ctx.stroke()
        ctx.setLineDash([])
        ctx.fillStyle = '#94A3B8'
        ctx.textAlign = 'right'
        ctx.fillText(v, PAD.left - 5, y + 4)
      }

      var step = data.length <= 7 ? 1 : data.length <= 30 ? 5 : 15
      ctx.textAlign = 'center'
      ctx.fillStyle = '#94A3B8'
      for (var i = 0; i < data.length; i++) {
        if (i % step === 0 || i === data.length - 1) {
          ctx.fillText(this.formatDate(data[i].date), sx(i), H - 5)
        }
      }

      var grad = ctx.createLinearGradient(0, PAD.top, 0, H - PAD.bottom)
      grad.addColorStop(0, 'rgba(27,75,130,0.22)')
      grad.addColorStop(1, 'rgba(27,75,130,0)')
      ctx.beginPath()
      ctx.moveTo(sx(0), sy(data[0].units))
      for (var i = 1; i < data.length; i++) {
        ctx.lineTo(sx(i), sy(data[i].units))
      }
      ctx.lineTo(sx(data.length - 1), H - PAD.bottom)
      ctx.lineTo(sx(0), H - PAD.bottom)
      ctx.closePath()
      ctx.fillStyle = grad
      ctx.fill()

      ctx.beginPath()
      ctx.strokeStyle = CHART_NAVY
      ctx.lineWidth = 2.5
      ctx.lineJoin = 'round'
      ctx.lineCap = 'round'
      for (var i = 0; i < data.length; i++) {
        if (i === 0) ctx.moveTo(sx(i), sy(data[i].units))
        else ctx.lineTo(sx(i), sy(data[i].units))
      }
      ctx.stroke()

      if (hIdx !== null && hIdx >= 0 && hIdx < data.length) {
        var hx = sx(hIdx)
        var hy = sy(data[hIdx].units)

        ctx.strokeStyle = 'rgba(27,75,130,0.2)'
        ctx.lineWidth = 1
        ctx.setLineDash([4, 3])
        ctx.beginPath()
        ctx.moveTo(hx, PAD.top)
        ctx.lineTo(hx, H - PAD.bottom)
        ctx.stroke()
        ctx.setLineDash([])

        ctx.beginPath()
        ctx.arc(hx, hy, 6, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(27,75,130,0.15)'
        ctx.fill()

        ctx.beginPath()
        ctx.arc(hx, hy, 3.5, 0, Math.PI * 2)
        ctx.fillStyle = CHART_NAVY
        ctx.fill()
        ctx.strokeStyle = 'white'
        ctx.lineWidth = 1.5
        ctx.stroke()
      }
    },
    onMouseMove: function(e) {
      var wrapper = this.$refs.chartWrapper
      var canvas = this.$refs.salesCanvas
      if (!wrapper || !canvas) return

      var data = this.chartData
      if (!data || data.length === 0) return

      var rect = canvas.getBoundingClientRect()
      var mx = e.clientX - rect.left
      var my = e.clientY - rect.top
      var W = wrapper.clientWidth
      var H = wrapper.clientHeight
      var PAD = CHART_PAD
      var cw = W - PAD.left - PAD.right

      this.hoveredIdx = Math.max(0, Math.min(data.length - 1, Math.round((mx - PAD.left) / cw * (data.length - 1))))
      this.drawChart(this.hoveredIdx)

      var pt = data[this.hoveredIdx]
      this.tooltipDate = this.formatDate(pt.date)
      this.tooltipValue = pt.units + ' units sold'
      var estRevenue = Math.round(pt.units * this.estimatedUnitPrice)
      this.tooltipSub = '-' + this.currencySymbol + estRevenue.toLocaleString() + ' est. revenue'

      var tW = 168
      var tH = 70
      var left = mx + 14
      var top = my - tH / 2
      if (left + tW > W - 8) left = mx - tW - 14
      if (top < 4) top = 4
      if (top + tH > H - 4) top = H - tH - 4

      var tooltip = this.$refs.chartTooltip
      if (tooltip) {
        tooltip.style.left = left + 'px'
        tooltip.style.top = top + 'px'
      }
      this.tooltipVisible = true
    },
    onMouseLeave: function() {
      this.tooltipVisible = false
      this.hoveredIdx = null
      this.drawChart(null)
    }
  },
  watch: {
    chartData: function() {
      var self = this
      this.$nextTick(function() { self.drawChart(null) })
    }
  },
  mounted: function() {
    var self = this
    this.$nextTick(function() { self.drawChart(null) })
    this._resizeHandler = function() { self.drawChart(self.hoveredIdx) }
    window.addEventListener('resize', this._resizeHandler)
  },
  beforeDestroy: function() {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
    }
  }
}
</script>



// WEBPACK FOOTER //
// SalesVolumeChart.vue?29f69291